import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { usePushSetup } from '../../hooks/usePushSetup';
import type { PushDriver } from '../../lib/push-setup';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';

/**
 * Grouped by what the user is choosing between, not by column name: which events
 * are worth telling them about, and separately how those get delivered.
 */
const KIND_TOGGLES = [
  {
    key: 'mentionsEnabled',
    label: 'Mentions',
    description: 'When someone @-mentions you in a comment or task.',
  },
  {
    key: 'assignmentsEnabled',
    label: 'Assignments',
    description: 'When a task is assigned to you, by a person or an automation.',
  },
  {
    key: 'remindersEnabled',
    label: 'Reminders',
    description: 'Ahead of a task due date.',
  },
  {
    key: 'overdueEnabled',
    label: 'Overdue',
    description: 'When one of your tasks passes its due date.',
  },
] as const;

export interface NotificationPreferencesPanelProps {
  driver: PushDriver;
  /** Reveals the delivery self-test. Off in production UI; it writes a real inbox row. */
  showDiagnostics?: boolean;
}

export function NotificationPreferencesPanel({
  driver,
  showDiagnostics = false,
}: NotificationPreferencesPanelProps) {
  const { data: preferences, isLoading } = trpc.notifications.preferences.useQuery();
  const utils = trpc.useUtils();
  const updatePreferences = trpc.notifications.updatePreferences.useMutation({
    onSuccess: (next) => utils.notifications.preferences.setData(undefined, next),
  });
  const { status, actionable, enable, disable, busy, error, diagnostics } = usePushSetup(driver);

  const sendTestPush = trpc.notifications.sendTestPush.useMutation();
  const [testError, setTestError] = useState<string | null>(null);

  if (isLoading || !preferences) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold">Notify me about</h3>
          <p className="text-xs text-muted-foreground">
            Turning one off stops both the in-app entry and any desktop alert for it.
          </p>
        </div>
        {KIND_TOGGLES.map((toggle) => (
          <div key={toggle.key} className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Label htmlFor={toggle.key} className="text-sm">
                {toggle.label}
              </Label>
              <p className="text-xs text-muted-foreground">{toggle.description}</p>
            </div>
            <Switch
              id={toggle.key}
              checked={preferences[toggle.key]}
              disabled={updatePreferences.isPending}
              onCheckedChange={(checked) => updatePreferences.mutate({ [toggle.key]: checked })}
            />
          </div>
        ))}
      </section>

      <section className="space-y-3 border-t border-border pt-6">
        <div>
          <h3 className="text-sm font-semibold">Desktop alerts</h3>
          <p className="text-xs text-muted-foreground">
            Alerts show up even when this tab is closed. Each browser registers separately.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={status === 'ready' ? 'outline' : 'default'}
            size="sm"
            disabled={!actionable || busy}
            onClick={() => void (status === 'ready' ? disable() : enable())}
          >
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {status === 'ready' ? 'Turn off for this browser' : 'Turn on desktop alerts'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {status === 'ready' && 'On and registered here.'}
            {status === 'never-enabled' && 'Not set up yet.'}
            {status === 'device-missing' && 'On for your account, but not this browser.'}
            {status === 'blocked' && 'Blocked in your browser settings.'}
            {status === 'unsupported' && 'Not available in this browser.'}
          </span>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </section>

      {showDiagnostics && (
        <section className="space-y-2 border-t border-border pt-6">
          <h3 className="text-sm font-semibold">Diagnostics</h3>
          <Button
            variant="outline"
            size="sm"
            disabled={sendTestPush.isPending}
            onClick={() => {
              setTestError(null);
              sendTestPush.mutate(undefined, {
                onError: (e) => setTestError(e.message),
              });
            }}
          >
            {sendTestPush.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Send test push
          </Button>
          {/* Every precondition is reported rather than silently swallowed, so
              "no push arrived" can be told apart from "no push was ever sent". */}
          {sendTestPush.data && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <dt>Blocked by</dt>
              <dd>{sendTestPush.data.blockedBy ?? 'nothing — it was sent'}</dd>
              <dt>Push enabled</dt>
              <dd>{String(sendTestPush.data.pushEnabled)}</dd>
              <dt>Active devices</dt>
              <dd>{sendTestPush.data.activeDeviceCount}</dd>
              <dt>Queued deliveries</dt>
              <dd>{sendTestPush.data.queuedDeliveries}</dd>
              <dt>Queue bound</dt>
              <dd>{String(sendTestPush.data.queueBound)}</dd>
              <dt>Backend FCM project</dt>
              <dd>{sendTestPush.data.fcmProjectId || '—'}</dd>
              <dt>This browser's project</dt>
              {/* A mismatch between these two is the classic silent failure. */}
              <dd>{diagnostics?.projectId || '—'}</dd>
              <dt>Token</dt>
              <dd>{diagnostics?.tokenTail ? `…${diagnostics.tokenTail}` : 'none'}</dd>
            </dl>
          )}
          {testError && <p className="text-xs text-destructive">{testError}</p>}
        </section>
      )}
    </div>
  );
}
